import { useState, useEffect } from "react";
import type { AxiosInstance } from "axios";
import type { SdwanHubDetailsResponse } from "../sdwan.types";
import { fetchSdwanHubDetails } from "../requests/request";
import { readCachedFetch, writeCachedFetch } from "@hooks/useCachedFetch";

/**
 * Load SD-WAN hub detail (Nivel 2):
 * GET /kontroledge/sdwan/:id?tenantId=
 * FortiManager data is composed live server-side — expect a few seconds on
 * first load (one multi-target call covers every branch).
 */
export function useSdwanHubDetails(
    sdwanId: string | null,
    api: AxiosInstance,
    refresh: number,
    tenantId: number | null
) {
    // The last answer for this hub paints at once when the user comes back (a view
    // switch, Back); the request below still runs and replaces it.
    const cacheKey =
        sdwanId && tenantId != null
            ? `kontroledge/sdwan/${sdwanId}?tenantId=${tenantId}`
            : null;
    const [raw, setRaw] = useState<SdwanHubDetailsResponse | null>(
        () => readCachedFetch<SdwanHubDetailsResponse>(cacheKey) ?? null
    );
    const [loading, setLoading] = useState(() => !readCachedFetch(cacheKey));
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (!sdwanId || tenantId == null) {
            setRaw(null);
            setLoading(false);
            setError(null);
            return;
        }

        let cancelled = false;
        // Another hub on the same mounted page: its own cached answer or the skeleton —
        // never the previous hub's data.
        const cached = readCachedFetch<SdwanHubDetailsResponse>(cacheKey);
        setRaw(cached ?? null);
        setLoading(!cached);
        setError(null);

        (async () => {
            try {
                const response = await fetchSdwanHubDetails(
                    api,
                    tenantId,
                    sdwanId
                );
                if (cancelled) return;
                const body = response.data;
                if (!body?.success || !body.data) {
                    throw new Error(
                        body?.message ?? "Failed to load SD-WAN hub"
                    );
                }
                writeCachedFetch(cacheKey, body.data);
                setRaw(body.data);
                setError(null);
            } catch (e) {
                console.error("Error in useSdwanHubDetails:", e);
                if (!cancelled) {
                    const status = (e as { response?: { status?: number } })
                        ?.response?.status;
                    // 404 → service not found (empty state), even over a cached answer.
                    if (status === 404) {
                        setRaw(null);
                        setError(null);
                        return;
                    }
                    // Other failures (500 / FMG down / network): a cached answer on
                    // screen stays there; with nothing to show → error state with retry.
                    if (!cached) {
                        setRaw(null);
                        setError(
                            "We couldn't load this SD-WAN service. Please try again."
                        );
                    }
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [sdwanId, tenantId, api, refresh, cacheKey]);

    return { raw, loading, error };
}
